/**
 * Hook para gerenciar fila de mensagens
 * Envia mensagens via webhook e mantém estado local
 * 
 * @version 1.1.0 - Adicionado suporte a preview na aplicação principal
 */

import React, { useState, useCallback, useRef } from 'react';
import type { ChatMessage, VisitorData, WebhookPayload, WebhookResponse } from '../types';
import { getSupabaseUrl } from '../utils/supabaseConfig';
import { getStorageUrl } from '../utils/storageUrl';
import { supabase } from '@/integrations/supabase/client';
import { createLogger } from '@/lib/logger';

// Logger para diagnóstico
const logger = createLogger('MessageQueue');

// Gera ID único para mensagem local
const generateMessageId = (): string => {
  return `msg_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
};

interface UseMessageQueueProps {
  inboxId: string;
  visitor: VisitorData | null;
}

export const useMessageQueue = ({ inboxId, visitor }: UseMessageQueueProps) => {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isSending, setIsSending] = useState(false);
  const [conversationId, setConversationId] = useState<string | null>(null);
  // Flag para evitar inicialização duplicada
  const isInitialized = useRef(false);

  // Adiciona mensagem recebida via Realtime
  const addMessage = useCallback((message: ChatMessage) => {
    setMessages((prev) => {
      // Evita duplicatas
      if (prev.some((m) => m.id === message.id)) {
        return prev;
      }
      return [...prev, message];
    });
  }, []);

  // Envia mensagem de texto
  const sendMessage = useCallback(async (content: string): Promise<boolean> => {
    if (!visitor || !content.trim()) return false;

    const localId = generateMessageId();
    const localMessage: ChatMessage = {
      id: localId,
      content: content.trim(),
      direction: 'inbound',
      sender_type: 'contact',
      sent_at: new Date().toISOString(),
      status: 'sent',  // UX otimista: exibe check imediatamente
    };
    
    setMessages((prev) => [...prev, localMessage]);
    setIsSending(true);

    try {
      // Obtém sessão do Supabase para incluir Authorization no modo autenticado
      // Isso permite validação consistente no backend (igual ao webchat-history)
      const { data: { session } } = await supabase.auth.getSession();
      
      const supabaseUrl = getSupabaseUrl();
      if (!supabaseUrl) {
        throw new Error('URL do Supabase não configurada');
      }

      // Payload no formato esperado pela edge function webchat-webhook
      const payload: WebhookPayload = {
        inbox_id: inboxId,
        visitor_id: visitor.visitor_id,
        message: {
          type: 'text',
          content: content.trim(),
        },
        visitor_data: {
          name: visitor.name,
          email: visitor.email,
          phone: visitor.phone,
        },
        page_url: window.location.href,
        referrer: document.referrer || undefined,
      };

      // Monta headers - inclui Authorization quando há sessão ativa
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };
      
      if (session?.access_token) {
        headers['Authorization'] = `Bearer ${session.access_token}`;
        logger.debug('Enviando mensagem com Authorization header (modo autenticado)');
      }

      const response = await fetch(`${supabaseUrl}/functions/v1/webchat-webhook`, {
        method: 'POST',
        headers,
        body: JSON.stringify(payload),
      });

      const data: WebhookResponse = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data.error || 'Falha ao enviar mensagem');
      }

      // Atualiza apenas o ID real da mensagem (status já é 'sent')
      setMessages((prev) => prev.map((m) => m.id === localId ? { ...m, id: data.message_id || localId } : m));
      if (data.conversation_id && !conversationId) setConversationId(data.conversation_id);

      return true;
    } catch (error) {
      console.error('[UaizyChat] Erro ao enviar mensagem:', error);
      setMessages((prev) => prev.map((m) => m.id === localId ? { ...m, status: 'failed' } : m));
      return false;
    } finally {
      setIsSending(false);
    }
  }, [inboxId, visitor, conversationId]);

  // Envia resposta de botão interativo (Flow Engine)
  const sendButtonResponse = useCallback(async (buttonId: string, buttonText: string): Promise<boolean> => {
    if (!visitor) return false;

    const localId = generateMessageId();
    const localMessage: ChatMessage = {
      id: localId,
      content: buttonText,
      direction: 'inbound',
      sender_type: 'contact',
      sent_at: new Date().toISOString(),
      status: 'sent',  // UX otimista: exibe check imediatamente
      metadata: { button_response: true, button_id: buttonId, button_text: buttonText },
    };
    
    setMessages((prev) => [...prev, localMessage]);
    setIsSending(true);

    try {
      // Obtém sessão do Supabase para incluir Authorization no modo autenticado
      const { data: { session } } = await supabase.auth.getSession();
      
      const supabaseUrl = getSupabaseUrl();
      if (!supabaseUrl) {
        throw new Error('URL do Supabase não configurada');
      }

      // Payload no formato esperado pela edge function webchat-webhook
      const payload: WebhookPayload = {
        inbox_id: inboxId,
        visitor_id: visitor.visitor_id,
        message: {
          type: 'button_response',
          content: buttonText,
          button_id: buttonId,
          button_text: buttonText,
        },
        visitor_data: {
          name: visitor.name,
          email: visitor.email,
          phone: visitor.phone,
        },
        page_url: window.location.href,
        referrer: document.referrer || undefined,
      };

      // Monta headers - inclui Authorization quando há sessão ativa
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };
      
      if (session?.access_token) {
        headers['Authorization'] = `Bearer ${session.access_token}`;
        logger.debug('Enviando resposta de botão com Authorization header');
      }

      const response = await fetch(`${supabaseUrl}/functions/v1/webchat-webhook`, {
        method: 'POST',
        headers,
        body: JSON.stringify(payload),
      });

      const data: WebhookResponse = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data.error || 'Falha ao enviar resposta');
      }

      // Atualiza apenas o ID real da mensagem (status já é 'sent')
      setMessages((prev) => prev.map((m) => m.id === localId ? { ...m, id: data.message_id || localId } : m));
      if (data.conversation_id && !conversationId) setConversationId(data.conversation_id);

      return true;
    } catch (error) {
      console.error('[UaizyChat] Erro ao enviar resposta de botão:', error);
      setMessages((prev) => prev.map((m) => m.id === localId ? { ...m, status: 'failed' } : m));
      return false;
    } finally {
      setIsSending(false);
    }
  }, [inboxId, visitor, conversationId]);

  // Envia arquivo (5.4 - Upload de arquivos)
  const sendFile = useCallback(async (file: File): Promise<boolean> => {
    if (!visitor) return false;

    const localId = generateMessageId();
    // Determina tipo de mídia para renderização local correta no widget
    // (image → preview, audio → player, video → player, demais → link de documento)
    const isImage = file.type.startsWith('image/');
    const isAudio = file.type.startsWith('audio/');
    const isVideo = file.type.startsWith('video/');
    const messageType: 'image' | 'audio' | 'video' | 'document' = isImage
      ? 'image'
      : isAudio
        ? 'audio'
        : isVideo
          ? 'video'
          : 'document';

    const localMessage: ChatMessage = {
      id: localId,
      // Áudio/imagem/vídeo não precisam exibir nome do arquivo na bolha
      content: messageType === 'document' ? file.name : '',
      direction: 'inbound',
      sender_type: 'contact',
      sent_at: new Date().toISOString(),
      status: 'sent',  // UX otimista: exibe check imediatamente
      attachments: [{
        type: messageType,
        url: URL.createObjectURL(file),
        filename: file.name,
        mime_type: file.type,
      }],
    };
    
    setMessages((prev) => [...prev, localMessage]);
    setIsSending(true);

    try {
      // Obtém sessão do Supabase para incluir Authorization no modo autenticado
      const { data: { session } } = await supabase.auth.getSession();
      
      // Converte arquivo para Base64 para envio
      const base64 = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });

      const supabaseUrl = getSupabaseUrl();
      if (!supabaseUrl) {
        throw new Error('URL do Supabase não configurada');
      }

      // Payload no formato esperado pela edge function webchat-webhook
      const payload: WebhookPayload = {
        inbox_id: inboxId,
        visitor_id: visitor.visitor_id,
        message: {
          // Tipo derivado do MIME: image | audio | video | document
          // O banco/edge function reconhece todos esses valores em messages.message_type
          type: messageType,
          content: file.name,
          file_base64: base64,
          file_name: file.name,
          file_type: file.type,
          file_size: file.size,
        },
        visitor_data: {
          name: visitor.name,
          email: visitor.email,
          phone: visitor.phone,
        },
        page_url: window.location.href,
        referrer: document.referrer || undefined,
      };

      // Monta headers - inclui Authorization quando há sessão ativa
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };
      
      if (session?.access_token) {
        headers['Authorization'] = `Bearer ${session.access_token}`;
        logger.debug('Enviando arquivo com Authorization header');
      }

      const response = await fetch(`${supabaseUrl}/functions/v1/webchat-webhook`, {
        method: 'POST',
        headers,
        body: JSON.stringify(payload),
      });

      const data: WebhookResponse = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data.error || 'Falha ao enviar arquivo');
      }

      // Revogar URL temporária
      const blobUrl = localMessage.attachments![0].url;
      
      // CORRIGIDO: Se backend retornou URL do storage, atualizar attachment
      if (data.attachment_url) {
        const storageUrl = getStorageUrl(data.attachment_url);
        
        // Só substituir e revogar se storageUrl for válida e diferente de blob:
        if (storageUrl && !storageUrl.startsWith('blob:')) {
          logger.debug('Atualizando attachment com URL do storage', { storageUrl });
          
          setMessages((prev) => prev.map((m) => {
            if (m.id === localId && m.attachments) {
              return {
                ...m,
                id: data.message_id || localId,
                attachments: m.attachments.map(att => ({
                  ...att,
                  url: storageUrl,
                })),
              };
            }
            return m;
          }));
          
          // Revogar blob URL após atualizar para URL persistente
          URL.revokeObjectURL(blobUrl);
        } else {
          // storageUrl inválida - manter blob e atualizar apenas o ID
          logger.warn('storageUrl inválida, mantendo blob URL', { storageUrl });
          setMessages((prev) => prev.map((m) => 
            m.id === localId ? { ...m, id: data.message_id || localId } : m
          ));
        }
        
      } else {
        // Backend não retornou attachment_url - manter blob (não revogar automaticamente)
        // A imagem pode sumir se revogar antes de Realtime trazer URL real
        logger.warn('Backend não retornou attachment_url, mantendo blob URL');
        setMessages((prev) => prev.map((m) => 
          m.id === localId ? { ...m, id: data.message_id || localId } : m
        ));
        // NOTA: blob URL vazará se Realtime nunca atualizar - tradeoff aceitável para UX
      }
      
      if (data.conversation_id && !conversationId) setConversationId(data.conversation_id);

      return true;
    } catch (error) {
      console.error('[UaizyChat] Erro ao enviar arquivo:', error);
      setMessages((prev) => prev.map((m) => m.id === localId ? { ...m, status: 'failed' } : m));
      return false;
    } finally {
      setIsSending(false);
    }
  }, [inboxId, visitor, conversationId]);

  // Reenvia mensagem com falha
  const retryMessage = useCallback(async (messageId: string): Promise<boolean> => {
    const message = messages.find((m) => m.id === messageId);
    if (!message || message.status !== 'failed') return false;

    // Remove mensagem antiga
    setMessages((prev) => prev.filter((m) => m.id !== messageId));
    
    // Reenvia
    return sendMessage(message.content);
  }, [messages, sendMessage]);

  // Limpa histórico de mensagens
  const clearMessages = useCallback(() => {
    setMessages([]);
    setConversationId(null);
    isInitialized.current = false;
  }, []);

  // Inicializa mensagens a partir do histórico carregado
  const initializeFromHistory = useCallback((
    historyMessages: ChatMessage[], 
    convId: string | null
  ) => {
    // Evita reinicialização se já tiver mensagens
    if (isInitialized.current) return;
    
    isInitialized.current = true;
    
    // Ordena mensagens do mais antigo para mais recente
    const sorted = [...historyMessages].sort(
      (a, b) => new Date(a.sent_at).getTime() - new Date(b.sent_at).getTime()
    );
    setMessages(sorted);
    if (convId) setConversationId(convId);
  }, []);

  // Adiciona mensagens mais antigas no início (para scroll infinito)
  const prependMessages = useCallback((olderMessages: ChatMessage[]) => {
    setMessages((prev) => {
      const sorted = [...olderMessages].sort(
        (a, b) => new Date(a.sent_at).getTime() - new Date(b.sent_at).getTime()
      );
      // Evita duplicatas
      const existingIds = new Set(prev.map(m => m.id));
      const uniqueOlder = sorted.filter(m => !existingIds.has(m.id));
      return [...uniqueOlder, ...prev];
    });
  }, []);

  return {
    messages,
    isSending,
    conversationId,
    addMessage,
    sendMessage,
    sendButtonResponse,
    sendFile,
    retryMessage,
    clearMessages,
    initializeFromHistory,
    prependMessages,
  };
};
